import { parseToolCallParamBindingRef } from '@cotera/utilities';
import { PersistedWorkflow } from '@cotera/sdk/core';
import type { UpstreamSourceGroup } from './variable-picker-data';

const { DATASET_RUN_INPUT_META_KEY } = PersistedWorkflow;

/**
 * True when the ref reaches its value by indexing INTO an array — any numeric
 * pointer segment, like the `0` in `/items/0/list_example`. Those paths pick one
 * element's sub-value, never a list you'd split against the source, so a group-by
 * must not offer them even when they happen to land on an array.
 */
function descendsThroughArrayIndex(ref: string): boolean {
  try {
    const { pointer } = parseToolCallParamBindingRef(ref);
    return pointer
      .split('/')
      .some((segment) => segment !== '' && /^\d+$/.test(segment));
  } catch {
    return false;
  }
}

/**
 * True when the ref traverses a dataset trigger's run-input `__meta` (its only
 * array is `__meta.changedFields`, which is plumbing, never a list to split).
 */
function traversesDatasetMeta(ref: string): boolean {
  try {
    const { pointer } = parseToolCallParamBindingRef(ref);
    return pointer.split('/').includes(DATASET_RUN_INPUT_META_KEY);
  } catch {
    return false;
  }
}

/**
 * Keep only the fields a group-by can actually split: top-level arrays. A
 * group-by needs a list, so showing `count`, `window` or a bare string beside
 * `items` is noise that invites pointing at a non-list — and a list reached by
 * indexing into another array (`items.0.list_example`), or buried in a dataset
 * trigger's `__meta` plumbing (its only array is `__meta.changedFields`), is
 * noise of a worse kind, naming something no one would group. Fields whose type
 * is unknown (no sample yet) are kept, since they may be arrays at run time.
 * Groups left with nothing to split drop out entirely.
 *
 * Stricter than {@link preferArrayUpstreamSources}, which only *reorders* arrays
 * to the front: a for-each tolerates a longer list, but a group-by's one job is
 * to split a list, so anything that is provably not one is just in the way.
 */
export function arrayOnlyUpstreamSources(
  sources: readonly UpstreamSourceGroup[]
): readonly UpstreamSourceGroup[] {
  return sources
    .map((group) => ({
      ...group,
      fields: group.fields.filter(
        (field) =>
          !descendsThroughArrayIndex(field.ref) &&
          !traversesDatasetMeta(field.ref) &&
          (field.value === undefined || Array.isArray(field.value))
      ),
    }))
    .filter((group) => group.fields.length > 0);
}

/**
 * Resolves a picked upstream field into group-by list config.
 *
 * Unlike for-each — which stores a dotted path and resolves it with `valueByPath`
 * — group-by resolves `itemsPath` with a JSON Pointer at run time, so the pointer
 * is kept in its `/a/b` form (`parseToolCallParamBindingRef` already returns it
 * that way). A root ref (the whole source value) leaves `itemsPath` undefined —
 * "split the whole output" — while still recording which upstream step it came
 * from so the runner reads from that ancestor rather than the direct input.
 */
export function groupByDataFromUpstreamRef(ref: string): {
  itemsPath?: string;
  sourceRunNodeId?: string;
} {
  const { sourceName, pointer } = parseToolCallParamBindingRef(ref);
  const data: { itemsPath?: string; sourceRunNodeId?: string } = {};
  if (pointer !== '') {
    data.itemsPath = pointer;
  }
  if (sourceName !== undefined && sourceName !== '') {
    data.sourceRunNodeId = sourceName;
  }
  return data;
}
